import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "react-oauth2-code-pkce";
import AIIconFullCBlack from "../../assets/AI_Icon_fullC_black.svg";
import AiChartPlaceholder from "../../assets/ai-chart-placeholder.svg";
import { getAppEnv } from "../../lib/getEnvVariables";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import AiPieChart from "./AiPieChart";
import { parseChartPayload } from "./aiChartUtils";
const AiAssistantDispatchEvent = {
    INVALID_CONFIGURATION: "ai-assistant-invalid-configuration",
    LOADED: "ai-assistant-loaded",
    NOTIFICATION_EVENT: "ai-assistant-notification-event",
    REDIRECTION_LOGIN: "ai-assistant-redirection-login",
    TOKEN_EXPIRED: "ai-assistant-token-expired",
    TOKEN_INVALID: "ai-assistant-token-invalid",
    TOKEN_PENDING_EXPIRATION: "ai-assistant-token-pending-expiration"
};
const AiAssistant = () => {
    const { logIn, token } = useContext(AuthContext);
    const [chartPayload, setChartPayload] = useState(null);
    const { primaryPartyType, userPartyNumber } = useGlobalStore((state) => ({
        primaryPartyType: state.primaryPartyType,
        userPartyNumber: state.userPartyNumber
    }));
    const { projectDetails } = useProjectDetailsStore((state) => ({
        projectDetails: state.projectDetails
    }));
    const resolvedAccessToken = useMemo(() => {
        if (token) {
            return token;
        }
        return localStorage.getItem("access_token")?.replace(/"/g, "") || "";
    }, [token]);
    /**
     * Constructs user preferences object for AI Assistant
     * Includes language and other user/default preferences in base64 encoded JSON format
     */
    const aiAssistantUserPreferences = useMemo(() => {
        const userProvided = {
            language: "en-US"
        };
        const defaults = {
            assetLabel: "name",
            assetLocationLabel: "address",
            currency: "USD",
            dateFormat: "ddmmyyyy",
            language: "en-US",
            numberFormat: "radixComa",
            pressureUnit: "kpa",
            systemOfMeasurement: "metric",
            temperatureUnit: "C",
            timeFormat: "hours12",
            timeZone: "UTC",
            windowsTimeZone: "GMT Standard Time"
        };
        return btoa(JSON.stringify({ defaults, userProvided }));
    }, []);
    /**
     * Constructs application context for AI Assistant
     * Currently includes the application name identifier
     */
    const aiAssistantAppContext = useMemo(() => {
        const isCatUser = primaryPartyType?.toUpperCase() === "CAT";
        const location = projectDetails?.streetAddress || "";
        const projectName = projectDetails?.projectName || "";
        return JSON.stringify({
            appName: "eflow.cat.com",
            conditions: {
                filters: {
                    downSelectPartyNumber: isCatUser ? userPartyNumber || "" : "",
                    location,
                    partyNumber: !isCatUser ? userPartyNumber || "" : "",
                    site_name: projectName,
                    subscribed: true
                }
            }
        });
    }, [primaryPartyType, projectDetails?.projectName, projectDetails?.streetAddress, userPartyNumber]);
    /**
     * Maps the app environment to the corresponding AI Assistant environment string.
     * The web component uses these values to construct the iframe URL:
     *   local → dev  (AI assistant not running locally; use dev instance)
     *   dev   → https://dev-ai-assistant.cat.com
     *   int   → https://int-ai-assistant.cat.com
     *   prod  → https://ai-assistant.cat.com
     */
    const aiAssistantEnv = useMemo(() => {
        const envMap = {
            dev: "dev",
            int: "int",
            local: "dev", // AI assistant not running locally; use dev instance
            prod: "prod"
        };
        return envMap[getAppEnv()] ?? "dev";
    }, []);
    /**
     * Registers AI Assistant event listeners for authentication and lifecycle events
     * Handles token expiration, authentication errors, and component lifecycle events
     * New in v1.2.0: LOADED and NOTIFICATION_EVENT handlers
     */
    useEffect(() => {
        const handleTokenExpired = (event) => {
            // Log the token has expired event
            console.log("AI Assistant: Token has Expired*****", event);
            // Relogin when token is expired
            if (logIn) {
                logIn();
            }
        };
        const handleTokenPendingExpiration = (event) => {
            // Log the token pending expiration event. react-oauth2-code-pkce lib should auto refresh the token
            console.log("AI Assistant: Token about to Expire*****", event);
        };
        const handleTokenInvalid = (event) => {
            // Log the invalid token details - Can enhance later
            console.log("AI Assistant: Invalid configuration*****", event.detail);
            // Trigger re-authentication
            if (logIn) {
                logIn();
            }
        };
        const handleRedirectionLogin = (event) => {
            // Log the redirection login event
            console.log("AI Assistant: Redirect to login*****", event);
            if (logIn) {
                logIn();
            }
        };
        // To debug invalid configuration passed to AI Assistant
        const handleInvalidConfiguration = (event) => {
            // Log the invalid configuration details - Can enhance later
            console.log("AI Assistant: Invalid configuration*****", event.detail);
        };
        const handleLoaded = (event) => {
            // Log when AI Assistant is loaded
            console.log("AI Assistant: Loaded*****", event);
        };
        const handleNotification = (event) => {
            const customEvent = event;
            console.log("AI Assistant: Notification Event*****", {
                detail: customEvent.detail,
                type: customEvent.type
            });
            const chart = parseChartPayload(customEvent.detail);
            if (chart)
                setChartPayload(chart);
        };
        // Add listeners for AI Assistant events using the enum values
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
        window.addEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
        window.addEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
        window.addEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
        window.addEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
        window.addEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
        return () => {
            // Cleanup event listeners
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_EXPIRED, handleTokenExpired);
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_PENDING_EXPIRATION, handleTokenPendingExpiration);
            window.removeEventListener(AiAssistantDispatchEvent.TOKEN_INVALID, handleTokenInvalid);
            window.removeEventListener(AiAssistantDispatchEvent.REDIRECTION_LOGIN, handleRedirectionLogin);
            window.removeEventListener(AiAssistantDispatchEvent.INVALID_CONFIGURATION, handleInvalidConfiguration);
            window.removeEventListener(AiAssistantDispatchEvent.LOADED, handleLoaded);
            window.removeEventListener(AiAssistantDispatchEvent.NOTIFICATION_EVENT, handleNotification);
        };
    }, [logIn]);
    const dismissChart = useCallback(() => {
        setChartPayload(null);
    }, []);
    const renderedChart = useMemo(() => {
        if (!chartPayload)
            return null;
        const { colors, labels, title, values } = chartPayload;
        if (!Array.isArray(labels) || !Array.isArray(values) || labels.length === 0 || values.length === 0) {
            return null;
        }
        return _jsx(AiPieChart, { colors: colors, labels: labels, title: title, values: values });
    }, [chartPayload]);
    return (_jsxs("div", { "data-test": "ai-insights-content", children: [_jsxs("div", { className: "flex items-center gap-2 mb-8", "data-test": "cat-ai-assistant-header", children: [_jsx("img", { alt: "AI Insights icon", className: "w-10 h-10", src: AIIconFullCBlack }), _jsx("h1", { className: "text-2xl font-bold tracking-tight", children: "AI Insights" }), renderedChart && (_jsx("button", { "aria-label": "Dismiss chart", className: "ml-4 text-xs text-gray-400 hover:text-gray-600", "data-test": "ai-chart-dismiss-btn", onClick: dismissChart, type: "button", children: "\u2715 Dismiss chart" }))] }), _jsxs("div", { className: "flex flex-col gap-6", "data-test": "ai-insights-content-container", children: [renderedChart ? (_jsx("div", { className: "w-full", "data-test": "ai-chart-container", children: renderedChart })) : (_jsxs("div", { className: "flex flex-col items-center justify-center py-16 text-gray-400", "data-test": "ai-chart-empty-state", children: [_jsx("img", { alt: "", "aria-hidden": "true", src: AiChartPlaceholder }), _jsx("p", { className: "mt-4 text-sm font-medium text-gray-500", children: "Use the AI assistant in the bottom-right corner to ask questions about your project" })] })), _jsx("div", { "data-test": "ai-insights-panel", children: _jsx(Fragment, { children: _jsx("cat-ai-assistant", { "access-token": resolvedAccessToken, "app-context": aiAssistantAppContext, "data-test": "cat-ai-assistant", environment: aiAssistantEnv, "x-cat-user-preferences": aiAssistantUserPreferences }) }, projectDetails?.projectName ?? "no-project") })] })] }));
};
export default AiAssistant;
